import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { EnergyConsumptionChartType } from "../../../core/enums/dashboard-enums";
import { getEnergyReports } from "../../../store/slices/fault-module/energy-report.slice";
import EnergyReportTableComponent from "./energy-report-table.component";

const EnergyReportComponent = () => {
  const [chartData, setChartData] = useState<any>({});
  const [selectedReportType, setSelectedReportType] = useState<any>(null);
  const [
    selectedEnergyConsumptionChartType,
    setSelectedEnergyConsumptionChartType,
  ] = useState<any>(null);
  const [energyConsumptionVsTimeData, setEnergyConsumptionVsTimeData] =
    useState<any>({
      energyConsumptionInWKH: [],
      savedEnergy: [],
      timeperiod: [],
      energyConsumptionBaseline: [],
    });
  const [
    energyConsumptionVsLevelAndLocationData,
    setEnergyConsumptionVsLevelAndLocationData,
  ] = useState<{
    seriesData: any[];
    xAxisValues: any[];
  }>({ seriesData: [], xAxisValues: [] });

  const [energyConsumptionVsSensorData, setEnergyConsumptionVsSensorData] =
    useState<{
      energyConsumed: any[];
      sensorNode: any[];
      extraData: any[];
    }>({
      energyConsumed: [],
      sensorNode: [],
      extraData: [],
    });

  const dispatch = useDispatch();

  const checkAllValuesForNull = (arr: any[]) => {
    return arr?.every((value) => value === null) ? null : arr;
  };

  const reportData = useSelector((state: any) => {
    return state.energyReport.energyReports;
  });

  const isLoadingReportData = useSelector((state: any) => {
    return state.energyReport.isLoading;
  });

  const resetValues = () => {
    setChartData({
      timePeriods: [],
      energyAmounts: [],
    });

    setEnergyConsumptionVsTimeData({
      energyConsumptionInWKH: [],
      savedEnergy: [],
      timeperiod: [],
      energyConsumptionBaseline: [],
    });

    setEnergyConsumptionVsLevelAndLocationData({
      seriesData: [],
      xAxisValues: [],
    });

    setEnergyConsumptionVsSensorData({
      energyConsumed: [],
      sensorNode: [],
      extraData: [],
    });
  }

  useEffect(() => {
    const responseData = reportData?.data || [];

    if (
      Number(selectedReportType) === 1 &&
      selectedEnergyConsumptionChartType &&
      responseData
    ) {
      const chartType = Number(selectedEnergyConsumptionChartType);

      if (chartType == EnergyConsumptionChartType.Energy_Vs_Time) {
        if (checkAllValuesForNull(responseData?.energyConsumptionInWKH)) {
          const energyConsumptionBaseline = structuredClone(responseData?.energyConsumptionBaseline);
          const baselineData = energyConsumptionBaseline?.map(
            (val: any) => (val === 0 ? null : val)
          );

          setEnergyConsumptionVsTimeData({
            energyConsumptionInWKH: structuredClone(responseData?.energyConsumptionInWKH),
            savedEnergy: structuredClone(responseData?.savedEnergy),
            timeperiod: structuredClone(responseData?.timeperiod),
            energyConsumptionBaseline: baselineData,
          });
        } else {
          setEnergyConsumptionVsTimeData({
            energyConsumptionInWKH: [],
            savedEnergy: [],
            timeperiod: [],
            energyConsumptionBaseline: [],
          });
        }
      } else if (
        chartType == EnergyConsumptionChartType.Energy_Vs_Level ||
        chartType == EnergyConsumptionChartType.Energy_Vs_Location
      ) {
        const isPlainRecord = !Array.isArray(responseData);
        const { xAxisValues, seriesData } = isPlainRecord
          ? responseData
          : { xAxisValues: [], seriesData: [] };

        setEnergyConsumptionVsLevelAndLocationData({
          xAxisValues: structuredClone(xAxisValues),
          seriesData: structuredClone(seriesData),
        });
      } else if (chartType == EnergyConsumptionChartType.Energy_Vs_Sensor) {
        if (!Array.isArray(responseData)) {
          setEnergyConsumptionVsSensorData({
            energyConsumed: [],
            sensorNode: [],
            extraData: [],
          });
        } else {
          const consumedEnergy = responseData?.map(
            (item: any) => item?.EnergyConsumption
          );

          const sensorNode = responseData?.map(
            (item: any) => item?.assetDisplayName
          );

          const extraData = responseData?.map((d: any) => {
            return {
              code: d?.assetCode,
              displayName: d?.assetDisplayName,
              energySaved: d?.EnergySaved,
              energyConsuption: d?.EnergyConsumption,
              level: d?.level,
            };
          });

          setEnergyConsumptionVsSensorData({
            energyConsumed: structuredClone(consumedEnergy),
            sensorNode: structuredClone(sensorNode),
            extraData: structuredClone(extraData),
          });
        }
      }
    } else if (Array.isArray(responseData) && responseData.length) {
      const filteredData = responseData?.filter(
        (item: any) => item?.energyConsumptionInWKH != null
      );

      setChartData({
        timePeriods: filteredData?.map((item: any) => item?.timePeriod),
        energyAmounts: filteredData?.map(
          (item: any) => item?.energyConsumptionInWKH
        ),
      });
    } else {
      resetValues();
    }
  }, [reportData, selectedReportType, selectedEnergyConsumptionChartType]);

  const onRefetchData = (
    tcCode = null,
    siteCode = null,
    blockCode = null,
    reportType = null,
    energyConsumptionChartType = null,
    startDate = null,
    endDate = null,
    timePeriod = 1,
    asset = null,
    energyRate = null
  ) => {
    setSelectedReportType(reportType);
    setSelectedEnergyConsumptionChartType(energyConsumptionChartType);

    dispatch(
      getEnergyReports({
        tcCode,
        siteCode,
        blockCode,
        reportType,
        energyConsumptionChartType,
        startDate,
        endDate,
        timePeriod,
        asset,
        energyRate
      })
    );
  };

  const onResetForm = () => {
    resetValues();
  }

  return (
    <div>
      <EnergyReportTableComponent
        chartData={chartData}
        energyConsumptionVsTimeData={energyConsumptionVsTimeData}
        energyConsumptionVsLevelAndLocationData={energyConsumptionVsLevelAndLocationData}
        energyConsumptionVsSensorData={energyConsumptionVsSensorData}
        onRefetchData={onRefetchData}
        onResetForm={onResetForm}
        isLoading={isLoadingReportData}
      />
    </div>
  );
};

export default EnergyReportComponent;
